import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

/* ── AI credits & routing (ADR-0035) — amounts are credits (1 credit = $0.004) except *Usd ── */
export type AiBlockedReason = null | "AI_DISABLED" | "AI_NOT_ALLOWED" | "AI_NO_CREDITS" | "AI_USER_LIMIT";

export type AiCreditsMeDto = {
  configured: boolean;
  enabled: boolean;
  allowed: boolean;
  blocked: AiBlockedReason;
  lowBalance: boolean;
  /** null = unlimited */
  remainingCredits: number | null;
  monthlyAllowanceCredits: number | null;
  usedCredits: number;
  purchasedCredits: number;
  userLimitCredits: number | null;
  userUsedCredits: number;
};

export type AiWalletDto = {
  enabled: boolean;
  accessMode: "all" | "selected";
  monthlyAllowanceCredits: number | null;
  usedCredits: number;
  purchasedCredits: number;
  remainingCredits: number | null;
  defaultUserLimitCredits: number | null;
  lowBalanceCredits: number | null;
  lowBalance: boolean;
  costUsd: number;
};

export type AiWalletTransactionDto = {
  id: string;
  kind: "grant" | "purchase" | "adjust" | "allowance" | string;
  credits: number;
  note: string | null;
  actorName: string | null;
  createdAt: string;
};

export type AiPurchaseDto = {
  id: string;
  credits: number;
  note: string | null;
  status: "pending" | "approved" | "rejected" | "cancelled" | string;
  requestedByName: string | null;
  createdAt: string;
  decidedAt?: string | null;
};

export type AiCreditsOrgDto = {
  wallet: AiWalletDto;
  members: Array<{
    userId: string;
    displayName: string;
    email: string;
    roles: string[];
    isAllowed: boolean;
    monthlyLimitCredits: number | null;
    usedCredits: number;
  }>;
  usageByFeature: Array<{ feature: string; label: string; requests: number; credits: number }>;
  transactions: AiWalletTransactionDto[];
  purchases: AiPurchaseDto[];
};

export type AiEffort = "low" | "medium" | "high" | "xhigh" | "max";

export type AiFeatureSettingDto = {
  feature: string;
  label: string;
  enabled: boolean;
  model: string | null;
  effort: AiEffort | null;
  allowedModels: string[];
  userChoice: boolean;
  monthlyBudgetUsd: number | null;
  monthRequests: number;
  monthCostUsd: number;
};

export type PlatformAiDto = {
  defaultModel: string | null;
  models: Array<{ id: string; label: string }>;
  features: AiFeatureSettingDto[];
  tenants: Array<{
    tenantId: string;
    name: string;
    code: number;
    enabled: boolean;
    monthlyAllowanceCredits: number | null;
    usedCredits: number;
    purchasedCredits: number;
    monthRequests: number;
    monthCostUsd: number;
  }>;
  purchases: Array<AiPurchaseDto & { tenantId: string; tenantName: string; tenantCode: number | null }>;
};

export type PlatformTenantWalletDto = {
  wallet: AiWalletDto;
  transactions: AiWalletTransactionDto[];
  purchases: AiPurchaseDto[];
};

const item = <T>(p: Promise<{ data: { item: T } }>) => wrap(async () => (await p).data.item);

export const aiCreditsApi = {
  me(): Promise<AiCreditsMeDto> {
    return item(httpClient.get<{ item: AiCreditsMeDto }>("/ai/credits/me"));
  },
  org(): Promise<AiCreditsOrgDto> {
    return item(httpClient.get<{ item: AiCreditsOrgDto }>("/ai/credits/org"));
  },
  setPolicy(body: { accessMode: "all" | "selected"; defaultUserLimitCredits: number | null; lowBalanceCredits: number | null }): Promise<AiWalletDto> {
    return item(httpClient.put<{ item: AiWalletDto }>("/ai/credits/org/policy", body));
  },
  setMember(userId: string, body: { isAllowed: boolean; monthlyLimitCredits: number | null }) {
    return item(httpClient.put<{ item: { userId: string; isAllowed: boolean; monthlyLimitCredits: number | null } }>(`/ai/credits/org/members/${userId}`, body));
  },
  requestPurchase(body: { credits: number; note?: string | null }) {
    return item(httpClient.post<{ item: { id: string; status: string; credits: number } }>("/ai/credits/org/purchases", body));
  },
  cancelPurchase(id: string) {
    return item(httpClient.post<{ item: { id: string; status: string } }>(`/ai/credits/org/purchases/${id}/cancel`, {}));
  },
  platform: {
    overview(): Promise<PlatformAiDto> {
      return item(httpClient.get<{ item: PlatformAiDto }>("/platform/ai"));
    },
    updateFeature(
      feature: string,
      body: { enabled: boolean; model: string | null; effort: AiEffort | null; allowedModels: string[]; userChoice: boolean; monthlyBudgetUsd: number | null },
    ) {
      return item(httpClient.put<{ item: { feature: string } }>(`/platform/ai/features/${feature}`, body));
    },
    decidePurchase(id: string, decision: "approve" | "reject") {
      return item(httpClient.post<{ item: { id: string; status: string } }>(`/platform/ai/purchases/${id}/${decision}`, {}));
    },
    wallet(tenantId: string): Promise<PlatformTenantWalletDto> {
      return item(httpClient.get<{ item: PlatformTenantWalletDto }>(`/platform/tenants/${tenantId}/ai-wallet`));
    },
    saveWallet(tenantId: string, body: { enabled: boolean; monthlyAllowanceCredits: number | null; lowBalanceCredits: number | null }): Promise<AiWalletDto> {
      return item(httpClient.put<{ item: AiWalletDto }>(`/platform/tenants/${tenantId}/ai-wallet`, body));
    },
    adjust(tenantId: string, body: { credits: number; note?: string | null }): Promise<AiWalletDto> {
      return item(httpClient.post<{ item: AiWalletDto }>(`/platform/tenants/${tenantId}/ai-wallet/adjust`, body));
    },
  },
};
